import { useEffect, useRef } from "react";
import { useAuth } from "../auth/auth-context";
import { useAppStore } from "../state/app-store-context";
import { pullSnapshot, pushSnapshot, setTokenProvider } from "../lib/api";
import { toSyncData } from "../lib/sync";
import { setRetryHandler, setSyncStatus } from "./sync-status";
import type { AppState } from "../lib/db";

const PUSH_DEBOUNCE_MS = 1000;

/**
 * 登入後的雲端同步協調器：
 * - hydration 完成後只拉取一次遠端快照並合併進本機 state
 * - 本機資料改變後延遲推送（debounce），避免連續答題時頻繁請求
 * - 以 refs 記錄上次同步的序列化快照，避免拉取－推送循環
 */
export function CloudSync() {
  const { user, getToken } = useAuth();
  const { state, dispatch, ready } = useAppStore();

  const stateRef = useRef<AppState>(state);
  stateRef.current = state;
  const pulledRef = useRef(false);
  const lastSyncedRef = useRef<string | null>(null);
  const pushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pushCurrent = () => {
    setSyncStatus("syncing");
    const data = toSyncData(stateRef.current);
    const serialized = JSON.stringify(data);
    pushSnapshot(data).then((ok) => {
      if (ok) {
        lastSyncedRef.current = serialized;
        setSyncStatus("synced");
      } else {
        setSyncStatus("error");
      }
    });
  };

  // 登入後安裝 Token provider；hydration 完成後拉取一次
  useEffect(() => {
    if (!user) {
      pulledRef.current = false;
      setSyncStatus("offline");
      return;
    }
    setTokenProvider(getToken);
    if (!ready || pulledRef.current) return;
    pulledRef.current = true;
    setSyncStatus("syncing");
    pullSnapshot().then((remote) => {
      if (remote?.data) {
        dispatch({ type: "mergeRemote", data: remote.data });
      }
      // 記錄拉取完成當下的快照，未變動的內容不再推回雲端
      lastSyncedRef.current = JSON.stringify(toSyncData(stateRef.current));
      setSyncStatus("synced");
    });
  }, [user, getToken, ready, dispatch]);

  // 本機資料改變後延遲推送
  useEffect(() => {
    if (!user || !ready || !pulledRef.current) return;
    // 首次拉取尚未完成：不做任何事，避免把拉取前的本機資料推上雲端
    if (lastSyncedRef.current === null) return;
    if (JSON.stringify(toSyncData(state)) === lastSyncedRef.current) return;
    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => {
      pushTimerRef.current = null;
      pushCurrent();
    }, PUSH_DEBOUNCE_MS);
    return () => {
      if (pushTimerRef.current) {
        clearTimeout(pushTimerRef.current);
        pushTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, ready, state]);

  // 失敗後可從設定頁重試
  useEffect(() => {
    if (!user) return;
    setRetryHandler(pushCurrent);
    return () => {
      setRetryHandler(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return null;
}
